import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

// GDPR / India DPDP Act 2023 / US CCPA-CPRA compliant consent dialog shown before form submission.
const ConsentDialog = ({ open, onOpenChange, onConsent, isSubmitting }) => {
  const [agreed, setAgreed] = useState(false);

  const handleAgree = () => {
    if (!agreed) return;
    onConsent();
  };

  const handleOpenChange = (next) => {
    if (!next) setAgreed(false);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="consent-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <ShieldCheck className="text-red-600" size={24} />
          </div>
          <DialogTitle className="text-xl">Consent to Process Your Data</DialogTitle>
          <DialogDescription className="text-sm text-gray-600 leading-relaxed pt-2">
            In accordance with the India Digital Personal Data Protection Act, 2023 (DPDP) and
            applicable U.S. privacy laws (including the CCPA/CPRA), we need your consent before
            processing the personal information you have provided.
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm text-gray-600 leading-relaxed space-y-3 max-h-52 overflow-y-auto pr-1">
          <p>
            <span className="font-semibold text-gray-800">Purpose:</span> NIVA Digital, LLC will use
            the details you submit (such as your name, email, company, and message) solely to respond
            to your enquiry and to communicate with you about our services.
          </p>
          <p>
            <span className="font-semibold text-gray-800">Your rights:</span> You may access, correct,
            or request deletion of your data, and withdraw this consent at any time by contacting
            <a href="mailto:info@nivadigital.io" className="text-red-600 hover:underline"> info@nivadigital.io</a>.
            We do not sell or share your personal information for cross-context behavioural advertising.
          </p>
          <p>
            For full details on how we handle your data, please review our{' '}
            <Link to="/privacy-policy" target="_blank" className="text-red-600 hover:underline">
              Privacy Policy
            </Link>.
          </p>
        </div>

        <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer bg-gray-50">
          <Checkbox
            checked={agreed}
            onCheckedChange={(v) => setAgreed(Boolean(v))}
            className="mt-0.5"
            data-testid="consent-checkbox"
          />
          <span className="text-sm text-gray-700">
            I consent to NIVA Digital processing my personal data for the purpose described above.
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            data-testid="consent-cancel-btn"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={handleAgree}
            disabled={!agreed || isSubmitting}
            data-testid="consent-agree-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Agree & Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentDialog;
